import type { VMSnapshot } from "@/hooks/use-vm-live-usage";

export type HealthLevel = "low" | "medium" | "high" | "unknown";

export const THRESHOLD_MEDIUM = 70;
export const THRESHOLD_HIGH = 90;

/**
 * Derives an overall health level from VM stats.
 * Evaluates CPU %, memory %, and disk % against shared thresholds.
 */
export function getHealthLevel(data: VMSnapshot | undefined): HealthLevel {
    if (!data) {
        return "unknown";
    }

    const { vm } = data;
    const memPercent = vm.memory.total_bytes > 0 ? (vm.memory.used_bytes / vm.memory.total_bytes) * 100 : 0;
    const diskPercent = vm.disk.total_bytes > 0 ? (vm.disk.used_bytes / vm.disk.total_bytes) * 100 : 0;
    const metrics = [vm.cpu.usage_percent, memPercent, diskPercent];

    if (metrics.some((m) => m > THRESHOLD_HIGH)) {
        return "high";
    }
    if (metrics.some((m) => m > THRESHOLD_MEDIUM)) {
        return "medium";
    }
    return "low";
}

const TEXT_COLORS: Record<HealthLevel, string> = {
    low: "text-content-primary",
    medium: "text-content-orange",
    high: "text-content-destructive",
    unknown: "text-content-primary",
};

const LABELS: Record<HealthLevel, string> = {
    low: "Low",
    medium: "Medium",
    high: "High",
    unknown: "N/A",
};

export function healthTextColor(level: HealthLevel): string {
    return TEXT_COLORS[level];
}

export function healthLevelLabel(level: HealthLevel): string {
    return LABELS[level];
}
